import { useDeferredDateFieldInvalidity } from '@/hooks/useDeferredDateFieldInvalidity';
import type { TemplatePlaceholder } from '@/types';
import { getSectionsState, isBlurLeavingField } from '@/util';
import type { DateValidationError } from '@mui/x-date-pickers';
import type { FocusEvent } from 'react';
import { useRef, useState } from 'react';

export type RangeDateFieldValue = Date | null | undefined | TemplatePlaceholder;
export type RangeDateFieldChangeReason =
  | 'normal'
  | 'setIsTemplatePlaceholder'
  | 'unsetIsTemplatePlaceholder';

type Options = {
  /** The committed value currently held by the parent filter for this field. */
  value: RangeDateFieldValue;
  /** Record this field's invalidity (chrome + Apply) and re-report the combined filter validity. */
  reportInvalid: (isInvalid: boolean) => void;
  /** Push a new value to the parent filter state. */
  commit: (newValue: RangeDateFieldValue) => void;
};

/**
 * Blur-deferral for one date field of a from/to range picker.
 *
 * - Incomplete sections (no MUI onError) only mark the field invalid on blur.
 * - While focused, MUI's onChange(null) (a section of a valid date was cleared) is
 *   held back so the committed value isn't wiped from the filter mid-edit.
 * - Completely typed illegal dates still invalidate immediately via the change path.
 */
export function useDeferredRangeDateField({ value, reportInvalid, commit }: Options) {
  const isFocusedRef = useRef(false);
  const pendingNullRef = useRef(false);
  // MUI resyncs its sections to the `value` prop whenever it differs from the last
  // value it emitted. After a deferred onChange(null) the prop still holds the
  // committed date, so the picker must be shown `null` or the partial edit is reset.
  const [heldFrom, setHeldFrom] = useState<{ value: RangeDateFieldValue } | null>(null);

  const { handleError, handleChangeValidation, handleBlur, errorReason } =
    useDeferredDateFieldInvalidity(reportInvalid);

  // MUI only fires onError for completely typed illegal dates, so while focused it is
  // deferred like everything else. Unfocused (e.g. an illegal value supplied by the
  // parent on mount) there is nothing to wait for.
  const handleFieldError = (reason: DateValidationError | null) => {
    if (isFocusedRef.current) {
      handleError(reason);
    } else {
      handleChangeValidation(reason);
    }
  };

  const handleChange = (
    newValue: RangeDateFieldValue,
    reason: RangeDateFieldChangeReason,
    validationError?: DateValidationError | null
  ) => {
    if (reason === 'normal' && newValue === null && !validationError && isFocusedRef.current) {
      pendingNullRef.current = true;
      setHeldFrom({ value });
      return;
    }
    pendingNullRef.current = false;
    setHeldFrom(null);
    handleChangeValidation(validationError ?? null);
    commit(newValue);
  };

  const onFocus = () => {
    isFocusedRef.current = true;
  };

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!isBlurLeavingField(event)) return;
    isFocusedRef.current = false;

    const sectionsState = getSectionsState(event.currentTarget);

    if (sectionsState === 'partial') {
      // The committed value (if any) is left in the filter; only validity changes.
      handleError(errorReason ?? 'invalidDate');
      handleBlur();
    } else if (sectionsState === 'empty' && (pendingNullRef.current || errorReason)) {
      // Genuinely empty: a deferred onChange(null), or a previously partial/invalid
      // field that was cleared (null -> null, so MUI emits no onChange).
      pendingNullRef.current = false;
      setHeldFrom(null);
      handleChangeValidation(null);
      commit(null);
    } else {
      handleBlur();
    }
  };

  // Value to give the picker: null while an incomplete edit of `value` is held back.
  const displayValue = heldFrom !== null && heldFrom.value === value ? null : value;

  return {
    displayValue,
    handleChange,
    handleError: handleFieldError,
    errorReason,
    textFieldSlotProps: { onFocus, onBlur },
  };
}
